// Resume Matching Tab Component
// Uses server-side Firebase Functions for AI processing (API keys not exposed to client)
import { useState, useRef, useEffect } from 'react';
import type { User } from 'firebase/auth';
import type { SavedResume } from '@shared/types';
import { getSavedResumes, deleteSavedResume, updateSavedResume, createSavedResume, getApplicationResumes, importResumeFromApplication } from '../services/resumeService';
import { processResume, reprocessResume, matchResumesToJob, batchProcessResumes, type MatchResult, type JDAnalysis } from '../services/resumeMatchingFunctions';

interface ResumeMatchingTabProps {
  user: User;
}

export default function ResumeMatchingTab({ user }: ResumeMatchingTabProps) {
  const [resumes, setResumes] = useState<SavedResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  const [matchResults, setMatchResults] = useState<MatchResult[] | null>(null);
  const [jdAnalysis, setJdAnalysis] = useState<JDAnalysis | null>(null);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newResumeName, setNewResumeName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [editingResume, setEditingResume] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [appResumes, setAppResumes] = useState<Array<{
    appId: string; company: string; jobTitle: string;
    resumeUrl: string; resumePath: string; appliedDate: any;
  }>>([]);
  const [importing, setImporting] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadResumes();
  }, [user.uid]);

  const loadResumes = async () => {
    try {
      setLoading(true);
      const savedResumes = await getSavedResumes(user.uid);
      setResumes(savedResumes);
    } catch (error) {
      console.error('Error loading resumes:', error);
      setMessage({ type: 'error', text: 'Failed to load resumes.' });
    } finally {
      setLoading(false);
    }
  };

  const isPDF = (file: File): boolean => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isPDF(file)) {
      setMessage({ type: 'error', text: 'Please upload a PDF file.' });
      return;
    }

    const resumeName = newResumeName.trim() || file.name.replace(/\.[^/.]+$/, '');

    setUploading(true);
    setMessage(null);

    try {
      // Step 1: Upload file and create Firestore document
      const savedResume = await createSavedResume(user.uid, file, resumeName);

      // Add to list immediately (with empty skills)
      setResumes((prev) => [savedResume, ...prev]);
      setNewResumeName('');

      // Step 2: Process resume server-side (extract text, skills, embedding)
      setProcessing(savedResume.id);
      const { skills } = await processResume(savedResume.id, savedResume.storagePath);

      // Update the resume in the list with the extracted skills
      setResumes((prev) =>
        prev.map((r) =>
          r.id === savedResume.id ? { ...r, skills } : r
        )
      );

      setMessage({
        type: 'success',
        text: `Resume "${resumeName}" uploaded and processed! Found ${skills.length} skills.`,
      });
    } catch (error: any) {
      console.error('Error uploading resume:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to upload and process resume.',
      });
    } finally {
      setUploading(false);
      setProcessing(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteResume = async (resume: SavedResume) => {
    if (!confirm(`Delete "${resume.name}"? This cannot be undone.`)) return;

    try {
      await deleteSavedResume(resume.id, resume.storagePath);
      setResumes((prev) => prev.filter((r) => r.id !== resume.id));
      setMessage({ type: 'success', text: `Resume "${resume.name}" deleted.` });
    } catch (error) {
      console.error('Error deleting resume:', error);
      setMessage({ type: 'error', text: 'Failed to delete resume.' });
    }
  };

  const handleReprocessResume = async (resume: SavedResume) => {
    setProcessing(resume.id);
    setMessage(null);

    try {
      const { skills } = await reprocessResume(resume.id);
      setResumes((prev) =>
        prev.map((r) =>
          r.id === resume.id ? { ...r, skills } : r
        )
      );
      setMessage({
        type: 'success',
        text: `Re-processed "${resume.name}". Found ${skills.length} skills.`,
      });
    } catch (error: any) {
      console.error('Error reprocessing resume:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to reprocess resume.',
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleMatchResumes = async () => {
    if (!jobDescription.trim()) {
      setMessage({ type: 'error', text: 'Please enter a job description.' });
      return;
    }

    if (resumes.length === 0) {
      setMessage({ type: 'error', text: 'Please upload at least one resume first.' });
      return;
    }

    setMatching(true);
    setMessage(null);
    setMatchResults(null);
    setJdAnalysis(null);

    try {
      const response = await matchResumesToJob(jobDescription);
      setMatchResults(response.results);
      setJdAnalysis(response.jdAnalysis);
      setMessage({
        type: 'success',
        text: `Matched ${response.results.length} resume(s) against the job description.`,
      });
    } catch (error: any) {
      console.error('Error matching resumes:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to match resumes.',
      });
    } finally {
      setMatching(false);
    }
  };

  const handleBatchProcess = async () => {
    setBatchProcessing(true);
    setMessage(null);

    try {
      const response = await batchProcessResumes();
      // Reload resumes to get updated skills
      await loadResumes();
      setMessage({
        type: 'success',
        text: `Batch processing complete: ${response.processed} processed, ${response.skipped} already done, ${response.failed} failed.`,
      });
    } catch (error: any) {
      console.error('Error batch processing:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to batch process resumes.',
      });
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleStartEdit = (resume: SavedResume) => {
    setEditingResume(resume.id);
    setEditName(resume.name);
  };

  const handleSaveEdit = async (resume: SavedResume) => {
    if (!editName.trim()) return;

    try {
      await updateSavedResume(resume.id, { name: editName.trim() });
      setResumes((prev) =>
        prev.map((r) =>
          r.id === resume.id ? { ...r, name: editName.trim() } : r
        )
      );
      setEditingResume(null);
    } catch (error) {
      console.error('Error updating resume name:', error);
      setMessage({ type: 'error', text: 'Failed to update resume name.' });
    }
  };

  const handleLoadAppResumes = async () => {
    try {
      setShowImport(true);
      // Reload saved resumes first to ensure we have the latest
      const savedResumes = await getSavedResumes(user.uid);
      setResumes(savedResumes);

      const apps = await getApplicationResumes(user.uid);
      // Filter out resumes already imported (by storagePath)
      const existingPaths = new Set(savedResumes.map((r) => r.storagePath));
      const filteredApps = apps.filter((a) => !existingPaths.has(a.resumePath));
      setAppResumes(filteredApps);

      if (filteredApps.length === 0 && apps.length > 0) {
        setMessage({ type: 'success', text: 'All application resumes are already imported.' });
      }
    } catch (error) {
      console.error('Error loading application resumes:', error);
      setMessage({ type: 'error', text: 'Failed to load application resumes.' });
    }
  };

  const handleImportResume = async (app: typeof appResumes[0]) => {
    setImporting(app.appId);
    try {
      const name = `${app.company} - ${app.jobTitle}`;
      const saved = await importResumeFromApplication(user.uid, name, app.resumeUrl, app.resumePath);
      setResumes((prev) => [saved, ...prev]);
      setAppResumes((prev) => prev.filter((a) => a.appId !== app.appId));
      setMessage({ type: 'success', text: `Imported "${name}". Use "Process All" to extract skills and embeddings.` });
    } catch (error: any) {
      console.error('Error importing resume:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to import resume.' });
    } finally {
      setImporting(null);
    }
  };

  const handleImportAll = async () => {
    setImporting('all');
    let imported = 0;
    try {
      for (const app of appResumes) {
        const name = `${app.company} - ${app.jobTitle}`;
        const saved = await importResumeFromApplication(user.uid, name, app.resumeUrl, app.resumePath);
        setResumes((prev) => [saved, ...prev]);
        imported++;
      }
      setAppResumes([]);
      setMessage({ type: 'success', text: `Imported ${imported} resume(s). Use "Process All" to extract skills and embeddings.` });
    } catch (error: any) {
      console.error('Error importing resumes:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to import some resumes.' });
    } finally {
      setImporting(null);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-600 dark:text-green-400';
    if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 70) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 50) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const getScoreBorderColor = (score: number): string => {
    if (score >= 70) return 'border-green-400 dark:border-green-600';
    if (score >= 50) return 'border-yellow-400 dark:border-yellow-600';
    return 'border-red-400 dark:border-red-600';
  };

  // Get match result for a specific resume
  const getMatchResultForResume = (resumeId: string): MatchResult | undefined => {
    return matchResults?.find(r => r.resumeId === resumeId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-4 sm:p-6 text-white">
        <h3 className="text-lg font-semibold mb-2">Resume Matching</h3>
        <p className="text-sm opacity-90">
          Upload multiple resumes and match them against job descriptions using AI.
          Get match scores and see which resume fits best for each job.
        </p>
      </div>

      {/* Upload New Resume */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Upload New Resume
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Resume Name (optional)
            </label>
            <input
              type="text"
              value={newResumeName}
              onChange={(e) => setNewResumeName(e.target.value)}
              placeholder="e.g., Frontend Developer Resume"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              uploading || processing
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
            }`}
            onClick={() => !uploading && !processing && fileInputRef.current?.click()}
          >
            {uploading || processing ? (
              <div className="flex flex-col items-center">
                <svg
                  className="animate-spin h-8 w-8 text-blue-500 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  {uploading ? 'Uploading...' : 'Processing resume...'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {uploading ? 'Uploading file to storage' : 'Extracting text and analyzing skills'}
                </p>
              </div>
            ) : (
              <>
                <svg
                  className="mx-auto h-8 w-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Click to upload a PDF resume
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  AI will extract skills and create an embedding for matching
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading || !!processing}
            />
          </div>
        </div>
      </div>

      {/* Import from Applications */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Import from Applications
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleLoadAppResumes}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {showImport ? 'Refresh' : 'Show Resumes'}
            </button>
            <button
              onClick={handleBatchProcess}
              disabled={batchProcessing || resumes.length === 0}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            >
              {batchProcessing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                'Process All'
              )}
            </button>
          </div>
        </div>
        {showImport && (
          appResumes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No new resumes found in your applications (already imported or none uploaded).
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  onClick={handleImportAll}
                  disabled={importing === 'all'}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {importing === 'all' ? 'Importing All...' : `Import All (${appResumes.length})`}
                </button>
              </div>
              <div className="space-y-2">
                {appResumes.map((app) => (
                  <div
                    key={app.appId}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {app.company} - {app.jobTitle}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {app.resumePath.split('/').pop()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleImportResume(app)}
                      disabled={!!importing}
                      className="ml-3 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      {importing === app.appId ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {/* Saved Resumes List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Your Resumes ({resumes.length})
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading resumes...</p>
        ) : resumes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No resumes uploaded yet. Upload your first resume above.
          </p>
        ) : (
          <div className="space-y-3">
            {resumes.map((resume) => {
              const matchResult = getMatchResultForResume(resume.id);
              return (
              <div
                key={resume.id}
                className={`border rounded-lg p-4 ${
                  matchResult
                    ? getScoreBorderColor(matchResult.matchScore)
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {editingResume === resume.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(resume);
                            if (e.key === 'Escape') setEditingResume(null);
                          }}
                        />
                        <button
                          onClick={() => handleSaveEdit(resume)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setEditingResume(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">
                          {resume.name}
                        </h4>
                        {/* Match Score Badge */}
                        {matchResult && (
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${getScoreBgColor(matchResult.matchScore)} ${getScoreColor(matchResult.matchScore)}`}>
                            {matchResult.matchScore}%
                          </span>
                        )}
                        <button
                          onClick={() => handleStartEdit(resume)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {resume.fileName} - {(resume.size / 1024).toFixed(1)} KB
                    </p>
                    {resume.skills.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {resume.skills.slice(0, 8).map((skill, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                          >
                            {skill}
                          </span>
                        ))}
                        {resume.skills.length > 8 && (
                          <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                            +{resume.skills.length - 8} more
                          </span>
                        )}
                      </div>
                    )}
                    {resume.skills.length === 0 && processing !== resume.id && (
                      <p className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
                        Skills not extracted yet. Click refresh to process.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={resume.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                      title="View"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </a>
                    <button
                      onClick={() => handleReprocessResume(resume)}
                      disabled={processing === resume.id}
                      className="p-2 text-gray-500 hover:text-green-600 dark:hover:text-green-400 disabled:opacity-50"
                      title="Re-process skills"
                    >
                      {processing === resume.id ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteResume(resume)}
                      className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Job Description Matching */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Match Against Job Description
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Paste Job Description
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>
          <button
            onClick={handleMatchResumes}
            disabled={matching || !jobDescription.trim() || resumes.length === 0}
            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
          >
            {matching ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Analyzing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Find Best Match
              </>
            )}
          </button>
        </div>
      </div>

      {/* JD Analysis */}
      {jdAnalysis && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Job Description Analysis
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Required Skills */}
            {jdAnalysis.skills.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Required Skills ({jdAnalysis.skills.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {jdAnalysis.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Keywords */}
            {jdAnalysis.keywords.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Important Keywords ({jdAnalysis.keywords.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {jdAnalysis.keywords.map((kw, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Phrases */}
            {jdAnalysis.suggestedPhrases.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Suggested Resume Phrases
                </p>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {jdAnalysis.suggestedPhrases.map((phrase, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-green-500 mt-0.5">•</span>
                      {phrase}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Role Requirements */}
            {jdAnalysis.roleRequirements.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role Requirements
                </p>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  {jdAnalysis.roleRequirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-orange-500 mt-0.5">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Match Results */}
      {matchResults && matchResults.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Match Results
          </h3>
          <div className="space-y-4">
            {matchResults.map((result, index) => (
              <div
                key={result.resumeId}
                className={`border rounded-lg p-4 ${
                  index === 0
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {index === 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-500 text-white rounded">
                        BEST MATCH
                      </span>
                    )}
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {result.resumeName}
                    </h4>
                  </div>
                  <div className={`text-2xl font-bold ${getScoreColor(result.matchScore)}`}>
                    {result.matchScore}%
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className={`rounded-lg p-3 ${getScoreBgColor(result.skillMatchScore)}`}>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Skill Match</p>
                    <p className={`text-lg font-semibold ${getScoreColor(result.skillMatchScore)}`}>
                      {result.skillMatchScore}%
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 ${getScoreBgColor(result.embeddingScore)}`}>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Semantic Match</p>
                    <p className={`text-lg font-semibold ${getScoreColor(result.embeddingScore)}`}>
                      {result.embeddingScore}%
                    </p>
                  </div>
                </div>

                {/* Matched Skills */}
                {result.matchedSkills.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Matched Skills ({result.matchedSkills.length}/{jdAnalysis ? jdAnalysis.skills.length : result.matchedSkills.length + (result.missingSkills?.length || 0)})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.matchedSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Skills */}
                {result.missingSkills && result.missingSkills.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Missing Skills ({result.missingSkills.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.missingSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Matched Keywords */}
                {result.matchedKeywords && result.matchedKeywords.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Matched Keywords ({result.matchedKeywords.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.matchedKeywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Keywords */}
                {result.missingKeywords && result.missingKeywords.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Missing Keywords ({result.missingKeywords.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.missingKeywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Experience Gaps */}
                {result.experienceGaps && result.experienceGaps.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Experience/Role Gaps ({result.experienceGaps.length})
                    </p>
                    <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
                      {result.experienceGaps.map((gap, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-red-500 mt-0.5">!</span>
                          {gap}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Embedding Status Warning */}
                {!result.hasEmbedding && (
                  <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-700 dark:text-yellow-300">
                    Embedding not generated. Semantic match unavailable. Click "Process All" to generate.
                  </div>
                )}

                {/* Action Button */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={result.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Resume
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
}
